// Billing — backend client + React Query hooks.
//
// Talks to the backend's /v1/billing/* routes (Phase 0a). The
// post-checkout flow also pushes the resulting status to the helper's
// PUT /v1/billing/status cache so the helper can enforce the trial gate
// without making an outbound call to the backend.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './ApiProvider';
import { useHelperClient } from './helper-queries';

export type SubTier = 'free' | 'pro' | 'team' | 'enterprise';

export interface BillingStatus {
  has_perpetual_license: boolean;
  license_purchased_at: string | null;
  sub_tier: SubTier;
  sub_current_period_end: string | null;
  trial_started_at: string | null;
  trial_days_remaining: number;
  stripe_customer_id: string | null;
}

export interface CheckoutSessionResponse {
  url: string;
}

export interface PortalSessionResponse {
  url: string;
}

export type Sku = 'desktop-license' | 'cloud-sub';

export const billingKeys = {
  status: ['billing', 'status'] as const,
};

export function useBillingStatus() {
  const api = useApi();
  return useQuery({
    queryKey: billingKeys.status,
    queryFn: () => api.get<BillingStatus>('/v1/billing/status'),
    // Trial countdown is per-day, so we refetch on focus + every 5 min
    // — enough to keep the banner accurate without spamming the backend.
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });
}

export function useCheckoutSession() {
  const api = useApi();
  return useMutation({
    mutationFn: (sku: Sku) =>
      api.post<CheckoutSessionResponse>('/v1/billing/checkout-session', { sku }),
  });
}

export function usePortalSession() {
  const api = useApi();
  return useMutation({
    mutationFn: () => api.post<PortalSessionResponse>('/v1/billing/portal-session', {}),
  });
}

/**
 * Push the current backend billing status into the helper's local cache.
 * Called after pairing and after every successful checkout. The helper uses
 * the cached value to enforce the 14-day trial + license gate on paid
 * endpoints. No-op if the helper isn't paired in this browser yet.
 */
export function useSyncBillingToHelper() {
  const api = useApi();
  const helper = useHelperClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const status = await api.get<BillingStatus>('/v1/billing/status');
      qc.setQueryData(billingKeys.status, status);
      if (helper) {
        await helper.putBillingStatus({
          has_perpetual_license: status.has_perpetual_license,
          license_purchased_at_ms: status.license_purchased_at
            ? new Date(status.license_purchased_at).getTime()
            : null,
          sub_tier: status.sub_tier,
          sub_current_period_end_ms: status.sub_current_period_end
            ? new Date(status.sub_current_period_end).getTime()
            : null,
          trial_started_at_ms: status.trial_started_at
            ? new Date(status.trial_started_at).getTime()
            : null,
        });
      }
      return status;
    },
  });
}
